import { createContext, useState, useEffect, useContext, ReactNode, useMemo } from 'react';

type Theme = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  theme: Theme;
  currentDisplayedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // Get the initial theme from localStorage or default to 'light'
  const getInitialTheme = (): Theme => {
    // Check if we're in the browser
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme') as Theme | null;
      return savedTheme || 'light';
    }
    return 'light';
  };
  
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [currentDisplayedTheme, setCurrentDisplayedTheme] = useState<'light' | 'dark'>('light');
  
  // Function to actually apply the theme to the HTML element
  const applyTheme = (newTheme: 'light' | 'dark') => {
    const root = window.document.documentElement;
    
    // Remove any existing theme classes
    root.classList.remove('light', 'dark');
    
    // Add the new theme class
    root.classList.add(newTheme);
    
    // Update the color-scheme
    root.style.colorScheme = newTheme;
    
    // Update state
    setCurrentDisplayedTheme(newTheme);
  };
  
  // Determine and apply the effective theme
  const updateTheme = (selectedTheme: Theme) => {
    // For 'auto' theme, check system preference
    if (selectedTheme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      applyTheme(prefersDark ? 'dark' : 'light');
    } else {
      // Directly apply the selected theme
      applyTheme(selectedTheme);
    }
  };

  // Initialize theme on first render
  useEffect(() => {
    updateTheme(theme);
  }, []);
  
  // Handle theme changes
  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('theme', theme);
    
    // Apply the theme
    updateTheme(theme);
    
    // Listen for system preference changes if theme is 'auto'
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        applyTheme(mediaQuery.matches ? 'dark' : 'light');
      };
      
      // Add event listener
      mediaQuery.addEventListener('change', handleChange);
      
      // Clean up
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [theme]);
  
  // Public setter for theme
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ theme, currentDisplayedTheme, setTheme }),
    [theme, currentDisplayedTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
